(() => {
  'use strict';
  const config = JSON.parse(document.getElementById('ThemeConfig')?.textContent || '{}');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const status = document.getElementById('ThemeStatus');
  const dialogTriggers = new WeakMap();
  const galleryStates = new WeakMap();
  let galleryPointer;
  let variantRequest;
  let predictiveRequest;
  let predictiveTimer;
  let cartBusy = false;

  function announce(text) {
    if (status) status.textContent = text;
  }

  function openDialog(dialog, trigger) {
    if (!dialog || typeof dialog.showModal !== 'function') return false;
    document.querySelectorAll('dialog[open]').forEach(other => {
      if (other !== dialog) other.close();
    });
    if (trigger) dialogTriggers.set(dialog, trigger);
    if (!dialog.open) dialog.showModal();
    document.body.classList.add('dialog-open');
    if (dialog.matches('[data-search-dialog]')) dialog.querySelector('input[type="search"]')?.focus();
    return true;
  }

  function initializeDialogs(scope = document) {
    scope.querySelectorAll('dialog').forEach(dialog => {
      if (dialog.dataset.initialized) return;
      dialog.dataset.initialized = 'true';
      dialog.addEventListener('close', () => {
        if (!document.querySelector('dialog[open]')) document.body.classList.remove('dialog-open');
        const trigger = dialogTriggers.get(dialog);
        if (trigger?.isConnected) trigger.focus({ preventScroll: true });
      });
      dialog.addEventListener('click', event => {
        if (event.target !== dialog) return;
        const box = dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) dialog.close();
      });
    });
  }

  function cartDialog() {
    return document.querySelector('[data-cart-dialog]');
  }

  function errorMessage(payload) {
    if (typeof payload?.description === 'string') return payload.description;
    if (typeof payload?.message === 'string') return payload.message;
    return config.strings?.error || 'Please try again.';
  }

  function showError(element, text) {
    if (!element) { announce(text); return; }
    element.textContent = text;
    element.hidden = false;
  }

  function renderCart(sections) {
    const mainCart = document.querySelector('[data-main-cart]');
    if (mainCart && sections?.[mainCart.dataset.sectionId]) {
      const page = new DOMParser().parseFromString(sections[mainCart.dataset.sectionId], 'text/html').querySelector('[data-main-cart]');
      if (page) mainCart.replaceWith(page);
    }
    const html = sections?.['hs-cart-drawer'];
    if (!html) return false;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const fresh = parsed.querySelector('[data-cart-dialog]');
    const current = cartDialog();
    if (!fresh || !current) return false;
    const note = current.querySelector('textarea[name="note"]')?.value;
    const focusKey = document.activeElement?.dataset?.key;
    const focusRole = document.activeElement?.getAttribute('data-cart-step');
    current.innerHTML = fresh.innerHTML;
    if (note !== undefined) {
      const newNote = current.querySelector('textarea[name="note"]');
      if (newNote) newNote.value = note;
    }
    const count = current.querySelector('[data-item-count]')?.dataset.itemCount;
    if (count !== undefined) document.querySelectorAll('[data-cart-count]').forEach(el => { el.textContent = count; });
    if (current.open && focusKey) {
      const candidates = [...current.querySelectorAll('[data-key]')];
      const focus = candidates.find(el => el.dataset.key === focusKey && el.getAttribute('data-cart-step') === focusRole);
      (focus || current.querySelector('[data-close-dialog]'))?.focus({ preventScroll: true });
    }
    initializeShippingGoals();
    return true;
  }


  function cartSections() {
    return ['hs-cart-drawer', document.querySelector('[data-main-cart]')?.dataset.sectionId].filter(Boolean);
  }

  function shippingThreshold(element) {
    const { baseCurrency, currency } = element.dataset;
    const threshold = Number(element.dataset.threshold);
    if (!(threshold > 0)) return null;
    if (baseCurrency === currency) return { amount: threshold, approximate: false };
    const rate = Number(window.Shopify?.currency?.rate);
    if (window.Shopify?.currency?.active !== currency || !(rate > 0) || !Number.isFinite(rate)) return null;
    return { amount: Math.ceil(threshold * rate), approximate: true };
  }

  function shippingMoney(minorUnits, currency) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(minorUnits / 100);
  }

  function initializeDeliverySummaries() {
    document.querySelectorAll('[data-delivery-summary]').forEach(summary => {
      const threshold = shippingThreshold(summary);
      if (!threshold) return;
      const currency = summary.dataset.currency;
      const rate = threshold.approximate ? Number(window.Shopify.currency.rate) : 1;
      const fee = Math.ceil(Number(summary.dataset.fee) * rate);
      const total = Number(summary.dataset.total) || 0;
      const remaining = Math.max(0, threshold.amount - total);
      const qualifies = summary.dataset.context === 'cart' && remaining === 0;
      const prefix = threshold.approximate ? '≈ ' : '';
      const feeText = prefix + shippingMoney(fee, currency);
      const thresholdText = prefix + shippingMoney(threshold.amount, currency);
      const rateText = qualifies ? (threshold.approximate ? summary.dataset.estimatedFreeText : summary.dataset.freeNowText) : summary.dataset.rateText.replace('__AMOUNT__', feeText);
      summary.querySelector('[data-delivery-rate]').textContent = rateText;
      summary.querySelector('[data-delivery-threshold]').textContent = summary.dataset.freeText.replace('__AMOUNT__', thresholdText);
      const remainder = summary.querySelector('[data-delivery-remaining]');
      remainder.hidden = summary.dataset.context !== 'cart' || remaining === 0;
      remainder.textContent = summary.dataset.remainingText.replace('__AMOUNT__', prefix + shippingMoney(remaining, currency));
      summary.querySelector('[data-delivery-estimate]').hidden = !threshold.approximate;
    });
  }

  function initializeShippingGoals() {
    initializeDeliverySummaries();
    document.querySelectorAll('[data-shipping-offer]').forEach(element => {
      const threshold = shippingThreshold(element);
      if (!threshold) return;
      const amount = (threshold.approximate ? '≈ ' : '') + shippingMoney(threshold.amount, element.dataset.currency);
      element.textContent = element.dataset.offerText.replace('__AMOUNT__', amount);
    });
    document.querySelectorAll('[data-shipping-goal]').forEach(goal => {
      const threshold = shippingThreshold(goal);
      const cards = [...goal.querySelectorAll('[data-cart-add-on]')];
      const additions = goal.querySelector('[data-cart-add-ons]');
      if (!threshold) { if (additions) additions.hidden = cards.length === 0; return; }
      const total = Number(goal.dataset.total) || 0;
      const remaining = Math.max(0, threshold.amount - total);
      const message = remaining > 0 ? (threshold.approximate ? goal.dataset.estimateText : goal.dataset.remainingText) : (threshold.approximate ? goal.dataset.estimatedReadyText : goal.dataset.readyText);
      goal.querySelector('[data-goal-text]').textContent = message.replace('__AMOUNT__', shippingMoney(remaining, goal.dataset.currency));
      const progress = goal.querySelector('[data-goal-progress]');
      progress.max = threshold.amount; progress.value = Math.min(total, threshold.amount); progress.hidden = false;
      if (additions) additions.hidden = remaining === 0 || cards.length === 0;
      const ranked = cards.sort((a, b) => Number(a.dataset.price) - Number(b.dataset.price));
      const chosen = ranked.find(card => Number(card.dataset.price) >= remaining) || ranked[0];
      cards.forEach(card => { card.hidden = card !== chosen; });
    });
  }

  async function refreshCart() {
    const url = new URL(config.cart || '/cart', location.origin);
    url.searchParams.set('sections', cartSections().join(','));
    const response = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(config.strings.error);
    return renderCart(await response.json());
  }

  async function changeCart(key, quantity) {
    if (cartBusy) return;
    cartBusy = true;
    const dialog = cartDialog();
    dialog?.setAttribute('aria-busy', 'true');
    const error = dialog?.querySelector('[data-cart-error]');
    if (error) error.hidden = true;
    try {
      const response = await fetch(`${config.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity, sections: cartSections(), sections_url: location.pathname })
      });
      const payload = await response.json();
      if (!response.ok || payload.status) throw new Error(errorMessage(payload));
      if (!renderCart(payload.sections)) await refreshCart();
      announce(config.strings.updated);
    } catch (err) {
      showError(dialog?.querySelector('[data-cart-error]'), err.message || config.strings.error);
    } finally {
      cartBusy = false;
      dialog?.removeAttribute('aria-busy');
    }
  }

  async function addProduct(form, submitter) {
    if (form.dataset.busy || cartBusy) return;
    form.dataset.busy = 'true';
    cartBusy = true;
    const button = submitter || form.querySelector('[type="submit"]');
    const original = button?.innerHTML;
    const error = form.querySelector('[data-form-error]');
    if (error) error.hidden = true;
    if (button) { button.disabled = true; button.setAttribute('aria-busy', 'true'); }
    const data = new FormData(form);
    data.set('sections', cartSections().join(','));
    data.set('sections_url', location.pathname);
    let added = false;
    try {
      announce(config.strings.adding);
      const response = await fetch(`${config.cartAdd}.js`, {
        method: 'POST', headers: { Accept: 'application/json' }, body: data
      });
      const payload = await response.json();
      if (!response.ok || payload.status) throw new Error(errorMessage(payload));
      added = true;
      let rendered = renderCart(payload.sections);
      if (!rendered) rendered = await refreshCart();
      announce(config.strings.added);
      if (!rendered || !openDialog(cartDialog(), button)) location.assign(config.cart);
    } catch (err) {
      if (added) { location.assign(config.cart); return; }
      showError(error, err.message || config.strings.error);
    } finally {
      delete form.dataset.busy;
      cartBusy = false;
      if (button) { button.disabled = false; button.removeAttribute('aria-busy'); if (original) button.innerHTML = original; }
    }
  }

  async function changeVariant(select) {
    const root = select.closest('[data-product-root]');
    if (!root) return;
    variantRequest?.abort();
    variantRequest = new AbortController();
    const request = variantRequest;
    const selection = select.value;
    const quantity = root.querySelector('[name="quantity"]')?.value;
    const plan = root.querySelector('[name="selling_plan"]')?.value;
    const form = root.querySelector('[data-product-form]');
    const submit = form?.querySelector('[type="submit"]');
    if (submit) submit.disabled = true;
    root.setAttribute('aria-busy', 'true');
    const url = new URL(root.dataset.productUrl, location.origin);
    url.searchParams.set('variant', selection);
    url.searchParams.set('section_id', root.dataset.sectionId);
    try {
      const response = await fetch(url, { signal: request.signal });
      if (!response.ok) throw new Error(config.strings.error);
      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fresh = parsed.querySelector('[data-product-root]');
      if (!fresh) throw new Error(config.strings.error);
      const info = fresh.querySelector('.product-info');
      if (!info || request.signal.aborted) return;
      root.querySelector('.product-info').replaceWith(info);
      initializeShippingGoals();
      root.dataset.variant = fresh.dataset.variant;
      if (fresh.dataset.variantMedia && fresh.dataset.variantMedia !== root.dataset.variantMedia) {
        showGalleryMedia(root, fresh.dataset.variantMedia);
      }
      root.dataset.variantMedia = fresh.dataset.variantMedia || '';
      const nextQuantity = info.querySelector('[name="quantity"]');
      if (nextQuantity && quantity) {
        const value = Number(quantity);
        const min = Number(nextQuantity.min) || 1;
        const step = Number(nextQuantity.step) || 1;
        const max = Number(nextQuantity.max) || Infinity;
        nextQuantity.value = Math.min(max, min + Math.max(0, Math.floor((value - min) / step)) * step);
      }
      const nextPlan = info.querySelector('[name="selling_plan"]');
      if (nextPlan && [...nextPlan.options].some(option => option.value === plan)) nextPlan.value = plan;
      const browserUrl = new URL(location.href);
      browserUrl.searchParams.set('variant', selection);
      history.replaceState({}, '', browserUrl);
      (info.querySelector(`[data-variant-value="${selection}"]`) || info.querySelector('[data-variant-select]'))?.focus({ preventScroll: true });
    } catch (err) {
      if (err.name === 'AbortError') return;
      const fallback = new URL(root.dataset.productUrl, location.origin);
      fallback.searchParams.set('variant', selection);
      location.assign(fallback);
    } finally {
      if (variantRequest === request) root.removeAttribute('aria-busy');
    }
  }

  function galleryState(root) {
    if (!galleryStates.has(root)) {
      const active = root.querySelector('[data-media-panel]:not([hidden])');
      galleryStates.set(root, { active, requested: active?.dataset.mediaPanel, token: 0, animations: [] });
    }
    return galleryStates.get(root);
  }

  function preloadGalleryNeighbor(root, panel) {
    const panels = [...root.querySelectorAll('[data-media-panel]')];
    const next = panels[(panels.indexOf(panel) + 1) % panels.length];
    const image = next?.querySelector('img');
    if (image) image.loading = 'eager';
  }

  function initializeGalleries(scope = document) {
    scope.querySelectorAll('[data-product-root]').forEach(root => {
      const state = galleryState(root);
      preloadGalleryNeighbor(root, state.active);
    });
  }

  async function showGalleryMedia(root, id, direction = 1) {
    if (!root) return;
    const panels = [...root.querySelectorAll('[data-media-panel]')];
    const next = panels.find(panel => panel.dataset.mediaPanel === String(id));
    const state = galleryState(root);
    if (!next || state.requested === String(id)) return;
    state.requested = String(id);
    const token = ++state.token;
    const image = next.querySelector('img');
    if (image) {
      image.loading = 'eager';
      try { await image.decode(); } catch { /* Keep navigation usable if a media source fails. */ }
    }
    if (state.token !== token || !root.isConnected) return;
    state.animations.forEach(animation => animation.cancel());
    state.animations = [];
    const previous = state.active;
    panels.forEach(panel => {
      panel.hidden = panel !== previous && panel !== next;
      panel.setAttribute('aria-hidden', String(panel !== next));
      if (panel !== next) panel.querySelectorAll('video').forEach(video => video.pause());
    });
    next.hidden = false;
    state.active = next;
    root.querySelectorAll('[data-media-id]').forEach(thumb => {
      if (thumb.dataset.mediaId === String(id)) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });
    const index = panels.indexOf(next) + 1;
    const counter = root.querySelector('[data-gallery-index]');
    if (counter) counter.textContent = String(index).padStart(2, '0');
    const message = root.querySelector('[data-gallery-status]');
    if (message) message.textContent = (config.strings?.galleryStatus || 'Image [current] of [total]').replace('[current]', index).replace('[total]', panels.length);
    if (previous && previous !== next && config.motion && !reducedMotion.matches && typeof next.animate === 'function') {
      const options = { duration: 360, easing: 'cubic-bezier(.22,.61,.36,1)', fill: 'both' };
      state.animations = [
        previous.animate([{ opacity: 1, transform: 'translateX(0)' }, { opacity: 0, transform: `translateX(${-10 * direction}px)` }], options),
        next.animate([{ opacity: 0, transform: `translateX(${14 * direction}px)` }, { opacity: 1, transform: 'translateX(0)' }], options)
      ];
      await Promise.allSettled(state.animations.map(animation => animation.finished));
    }
    if (state.token !== token) return;
    panels.forEach(panel => { panel.hidden = panel !== next; });
    state.animations.forEach(animation => animation.cancel());
    state.animations = [];
    preloadGalleryNeighbor(root, next);
  }

  function stepGallery(root, step) {
    if (!root) return;
    const panels = [...root.querySelectorAll('[data-media-panel]')];
    if (panels.length < 2) return;
    const state = galleryState(root);
    const current = Math.max(0, panels.findIndex(panel => panel.dataset.mediaPanel === state.requested));
    const next = panels[(current + step + panels.length) % panels.length];
    showGalleryMedia(root, next.dataset.mediaPanel, Math.sign(step));
  }

  async function predictiveSearch(input) {
    predictiveRequest?.abort();
    const results = document.querySelector('[data-predictive-results]');
    const term = input.value.trim();
    if (!results) return;
    if (term.length < 2) { results.innerHTML = ''; input.setAttribute('data-expanded', 'false'); return; }
    predictiveRequest = new AbortController();
    const url = new URL(config.predictiveSearch, location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', '6');
    url.searchParams.set('section_id', 'hs-predictive-search');
    results.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(url, { signal: predictiveRequest.signal });
      if (!response.ok) return;
      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      const content = parsed.querySelector('.predictive-content');
      if (content) { results.replaceChildren(content); input.setAttribute('data-expanded', 'true'); }
    } catch (err) {
      if (err.name !== 'AbortError') input.setAttribute('data-expanded', 'false');
    } finally { results.removeAttribute('aria-busy'); }
  }

  function initializeRecommendations(scope = document) {
    scope.querySelectorAll('[data-recommendations]').forEach(element => {
      if (element.dataset.initialized) return;
      element.dataset.initialized = 'true';
      const load = async () => {
        try {
          const response = await fetch(element.dataset.url);
          if (!response.ok) return;
          const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
          const fresh = parsed.querySelector('[data-recommendations]');
          if (fresh?.querySelector('.product-card')) { element.innerHTML = fresh.innerHTML; initializeMotion(element); }
          else element.hidden = true;
        } catch { element.hidden = true; }
      };
      if (!('IntersectionObserver' in window)) { load(); return; }
      const observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) { observer.disconnect(); load(); }
      }, { rootMargin: '350px' });
      observer.observe(element);
    });
  }

  function initializeMotion(scope = document) {
    if (!config.motion || reducedMotion.matches || !('IntersectionObserver' in window)) return;
    document.documentElement.classList.add('motion-ready');
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) { entry.target.classList.remove('reveal-pending'); entry.target.classList.add('is-visible'); observer.unobserve(entry.target); }
      });
    }, { threshold: 0.06, rootMargin: '0px 0px 20px 0px' });
    scope.querySelectorAll('[data-reveal]').forEach(element => {
      if (element.dataset.motionReady) return;
      element.dataset.motionReady = 'true';
      if (element.getBoundingClientRect().top > innerHeight) element.classList.add('reveal-pending');
      observer.observe(element);
    });
  }

  function initializeNavigation(pathname = location.protocol === 'file:' ? '/' : location.pathname) {
    const normalize = path => path.replace(/\/$/, '') || '/';
    const current = normalize(pathname);
    document.querySelectorAll('[data-nav-link]').forEach(link => {
      const target = normalize(new URL(link.href, location.href).pathname);
      const shopping = /\/(products|collections)\//.test(current);
      const mobileShop = link.closest('.mobile-bottom-nav') && target.endsWith('/collections/all') && shopping;
      const productShop = /\/products\//.test(current) && target.endsWith('/collections/all');
      if (current === target || mobileShop || productShop) link.setAttribute('aria-current', 'page');
      else link.removeAttribute('aria-current');
    });
  }

  function selectProductTab(button, focus = false) {
    const section = button.closest('section');
    if (!section) return;
    section.querySelectorAll('[data-product-tab]').forEach(tab => {
      const selected = tab === button;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
    });
    section.querySelectorAll('[data-product-panel]').forEach(panel => {
      panel.hidden = panel.dataset.productPanel !== button.dataset.productTab;
    });
    if (focus) button.focus({ preventScroll: true });
  }

  function initializeProductTabs(scope = document) {
    scope.querySelectorAll('[data-product-tabs]').forEach(tabs => {
      if (tabs.dataset.initialized) return;
      tabs.dataset.initialized = 'true';
      const first = tabs.querySelector('[data-product-tab][aria-selected="true"]') || tabs.querySelector('[data-product-tab]');
      if (first) selectProductTab(first);
    });
  }

  function initializeAddresses(scope = document) {
    scope.querySelectorAll('[data-address-country]').forEach(country => {
      if (country.dataset.initialized) return;
      country.dataset.initialized = 'true';
      if (country.dataset.default) country.value = country.dataset.default;
      const update = () => {
        const province = document.getElementById(country.dataset.province);
        if (!province) return;
        const values = JSON.parse(country.selectedOptions[0]?.dataset.provinces || '[]');
        province.replaceChildren(...values.map(pair => new Option(pair[1], pair[0])));
        if (province.dataset.default) { province.value = province.dataset.default; delete province.dataset.default; }
        province.closest('[data-province-container]').hidden = values.length === 0;
      };
      country.addEventListener('change', update);
      update();
    });
  }

  document.addEventListener('submit', event => {
    const form = event.target.closest('[data-product-form]');
    if (form && event.submitter?.name !== 'checkout') { event.preventDefault(); addProduct(form, event.submitter); }
  });

  document.addEventListener('click', event => {
    const button = event.target.closest('button,a');
    if (!button) return;
    if (button.hasAttribute('data-open-cart')) {
      if (openDialog(cartDialog(), button)) { event.preventDefault(); refreshCart().catch(() => {}); }
    } else if (button.hasAttribute('data-open-search')) {
      if (openDialog(document.querySelector('[data-search-dialog]'), button)) event.preventDefault();
    } else if (button.hasAttribute('data-close-dialog')) {
      button.closest('dialog')?.close();
    } else if (button.hasAttribute('data-product-tab')) {
      selectProductTab(button);
    } else if (button.hasAttribute('data-media-id')) {
      const root = button.closest('[data-product-root]');
      if (!root) return;
      event.preventDefault();
      showGalleryMedia(root, button.dataset.mediaId);
    } else if (button.hasAttribute('data-gallery-step')) {
      stepGallery(button.closest('[data-product-root]'), Number(button.dataset.galleryStep));
    } else if (button.hasAttribute('data-variant-value')) {
      const select = button.closest('[data-product-root]')?.querySelector('[data-variant-select]');
      if (select && select.value !== button.dataset.variantValue) {
        select.value = button.dataset.variantValue;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    } else if (button.hasAttribute('data-zoom-image')) {
      event.preventDefault();
      let dialog = document.querySelector('.image-dialog');
      if (!dialog) {
        dialog = document.createElement('dialog'); dialog.className = 'image-dialog';
        const close = document.createElement('button'); close.className = 'icon-button'; close.type = 'button';
        close.setAttribute('data-close-dialog', ''); close.setAttribute('aria-label', config.strings.close);
        close.textContent = '×'; dialog.append(close, document.createElement('img'));
        document.body.append(dialog); initializeDialogs();
      }
      const image = dialog.querySelector('img'); image.src = button.href;
      image.alt = button.parentElement.querySelector('img')?.alt || '';
      openDialog(dialog, button);
    } else if (button.hasAttribute('data-quantity-step')) {
      const input = button.closest('.quantity-picker')?.querySelector('input');
      if (input) { Number(button.dataset.quantityStep) > 0 ? input.stepUp() : input.stepDown(); input.dispatchEvent(new Event('change', { bubbles: true })); }
    } else if (button.hasAttribute('data-cart-step')) {
      const input = button.closest('.quantity-picker')?.querySelector('input');
      if (!input) return;
      const min = Number(input.dataset.lineMin) || 1;
      const step = Number(input.step) || 1;
      const current = Number(input.value) || 0;
      let quantity = Number(button.dataset.cartStep) > 0 ? (current === 0 ? min : current + step) : (current <= min ? 0 : Math.max(min, current - step));
      if (input.max) quantity = Math.min(quantity, Number(input.max));
      if (button.closest('[data-cart-dialog]')) changeCart(button.dataset.key, quantity);
      else input.value = quantity;
    } else if (button.hasAttribute('data-remove-item') && button.closest('[data-cart-dialog]')) {
      event.preventDefault(); changeCart(button.dataset.key, 0);
    } else if (button.hasAttribute('data-toggle-motion')) {
      const marquee = button.closest('.marquee');
      const paused = marquee.toggleAttribute('data-paused');
      button.setAttribute('aria-pressed', String(paused));
      button.textContent = paused ? '▶' : 'Ⅱ';
      button.setAttribute('aria-label', paused ? button.dataset.resume : button.dataset.pause);
    }
  });

  document.addEventListener('change', event => {
    if (event.target.matches('[data-localization-auto]')) event.target.closest('form')?.requestSubmit();
    if (event.target.matches('[data-variant-select]')) changeVariant(event.target);
    if (event.target.matches('[data-cart-quantity]') && event.target.closest('[data-cart-dialog]')) {
      const input = event.target;
      const quantity = Number(input.value);
      const min = Number(input.dataset.lineMin) || 1;
      if (Number.isInteger(quantity) && quantity >= 0 && (quantity === 0 || quantity >= min) && input.checkValidity()) changeCart(input.dataset.key, quantity);
      else input.reportValidity();
    }
  });

  document.addEventListener('input', event => {
    if (!event.target.matches('[data-predictive-input]')) return;
    clearTimeout(predictiveTimer);
    predictiveRequest?.abort();
    predictiveTimer = setTimeout(() => predictiveSearch(event.target), 220);
  });

  document.addEventListener('click', event => {
    document.querySelectorAll('[data-mobile-menu][open],.nav-dropdown[open]').forEach(menu => {
      if (!menu.contains(event.target)) menu.open = false;
    });
  });
  document.addEventListener('keydown', event => {
    if ((event.key === 'ArrowLeft' || event.key === 'ArrowRight') && event.target.matches('[data-product-tab]')) {
      event.preventDefault();
      const tabs = [...event.target.closest('[data-product-tabs]').querySelectorAll('[data-product-tab]')];
      const step = event.key === 'ArrowRight' ? 1 : -1;
      const next = tabs[(tabs.indexOf(event.target) + step + tabs.length) % tabs.length];
      selectProductTab(next, true);
      return;
    }
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      openDialog(document.querySelector('[data-search-dialog]'), document.activeElement);
      return;
    }
    if ((event.key === 'ArrowLeft' || event.key === 'ArrowRight') && event.target.closest('[data-gallery]') && !event.target.matches('input,textarea,select,video')) {
      event.preventDefault();
      stepGallery(event.target.closest('[data-product-root]'), event.key === 'ArrowRight' ? 1 : -1);
      return;
    }
    if (event.key !== 'Escape') return;
    document.querySelectorAll('[data-mobile-menu][open],.nav-dropdown[open]').forEach(menu => {
      menu.open = false; menu.querySelector('summary')?.focus();
    });
  });
  document.addEventListener('pointerdown', event => {
    if (event.pointerType === 'mouse' || event.target.closest('button,a,video,iframe,model-viewer')) return;
    const stage = event.target.closest('[data-gallery-stage]');
    if (stage) galleryPointer = { root: stage.closest('[data-product-root]'), id: event.pointerId, x: event.clientX, y: event.clientY };
  }, { passive: true });
  document.addEventListener('pointerup', event => {
    if (!galleryPointer || galleryPointer.id !== event.pointerId) return;
    const { root, x, y } = galleryPointer;
    galleryPointer = null;
    const horizontal = event.clientX - x;
    const vertical = event.clientY - y;
    if (Math.abs(horizontal) > 45 && Math.abs(horizontal) > Math.abs(vertical) * 1.4) stepGallery(root, horizontal < 0 ? 1 : -1);
  }, { passive: true });
  document.addEventListener('pointercancel', () => { galleryPointer = null; }, { passive: true });
  document.addEventListener('shopify:section:load', event => {
    initializeShippingGoals(); initializeDialogs(event.target); initializeProductTabs(event.target); initializeMotion(event.target); initializeRecommendations(event.target); initializeAddresses(event.target); initializeGalleries(event.target); initializeNavigation();
  });
  document.addEventListener('theme:navigate', event => initializeNavigation(event.detail?.pathname));
  window.addEventListener('pageshow', event => {
    if (event.persisted) refreshCart().catch(() => {});
  });
  if (!config.motion) document.querySelectorAll('.marquee').forEach(el => el.setAttribute('data-paused', ''));
  const recover = document.querySelector('.recover-password .form-success, .recover-password .errors');
  if (recover) recover.closest('details').open = true;
  initializeShippingGoals(); initializeDialogs(); initializeProductTabs(); initializeMotion(); initializeRecommendations(); initializeAddresses(); initializeGalleries(); initializeNavigation();
})();

